<script setup lang="ts">
type Props = {
  user: Record<string, any>;
};

const { user } = defineProps<Props>();

type Emits = {
  changeLoadingState: [boolean];
};

const emit = defineEmits<Emits>();

const DEFAULT_OFFSET_TOP: number = 25;

const { betBy } = useRuntimeConfig().public;
const { brandId, theme } = betBy;
const { isHomeAsSportbookEnabled } = useFeatureFlagsNew();

const { ApiService } = useApiService();

const {
  sportsPageBaseUrl,
  sportBookSlugFromUrl,
  betByRedirectToUrl,
  saniziteSlugUrlInBetby,
} = useSportsBook();

const betbyDiv = ref<HTMLDivElement | null>(null);
const locale = ref('pt-br');
const layoutStore = useLayoutStore();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin, openRegister } = useAuth();

const {
  isLayoutMobileOrTablet,
  topBarHeight,
  headerHeight,
  horizontalMenuHeight,
} = useLayout();

const stickyTop = computed<number>(() => {
  return headerHeight.value + topBarHeight.value;
});

const betSlipOffsetTop = computed<number>(() => {
  return topBarHeight.value + headerHeight.value + horizontalMenuHeight.value;
});

const betSlipBottom = computed<number>(() => {
  if (isLayoutMobileOrTablet.value) {
    // TODO: get auto height from MobileMenuBar.vue
    return 51;
  }

  return 0;
});

const hasChildrenDivs = computed<boolean>(() => {
  return !!betbyDiv.value && betbyDiv.value.childNodes.length > 1;
});

function onLogin() {
  openLogin();
}

function onRegister() {
  openRegister();
}

function onRouteChange(to: string) {
  if (hasChildrenDivs.value) {
    emit('changeLoadingState', false);
  }

  saniziteSlugUrlInBetby(to);
}

function onSessionRefresh() {
  location.reload();
}

function onRecharge() {
  navigateTo(localeLink('/?deposit=1'));
}

async function getToken() {
  if (layoutStore.menuOpen) {
    layoutStore.toggleMenuOpen();
  }

  if (user.user_id) {
    await ApiService.post(`/betby/get-jwt`, user)
      .then((res) => {
        startBetBy(res.data.jwt);
      })
      .catch((err) => {
        console.error(err);
      });
  } else {
    startBetBy();
  }
}

async function getNewToken() {
  if (!Object.keys(user).length)
    return await new Promise((resolve) => {
      resolve(false);
    });

  return await ApiService.post(`/betby/get-jwt`, user).then(
    (res) => res.data.jwt,
  );
}

function startBetBy(token?: string) {
  if (!import.meta.client) {
    return;
  }

  window.cactusEvents = window.cactusEvents || [];
  window.cactusEvents.push([
    'betby',
    () => {
      try {
        window.btRender = window.btRender || {};

        // @ts-ignore
        const betByFrame = new BTRenderer();

        const config = {
          brand_id: brandId,
          token: token || null,
          themeName: theme,
          lang: locale.value.toLowerCase(),
          target: document.getElementById('betby'),
          betSlipOffsetTop: betSlipOffsetTop.value + DEFAULT_OFFSET_TOP,
          betSlipOffsetBottom: betSlipBottom.value,
          stickyTop: stickyTop.value,
          betslipZIndex: 999,
          onTokenExpired: () => getNewToken(),
          onLogin,
          onRegister,
          onRouteChange,
          onSessionRefresh,
          onBetSlipStateChange: () => console.warn('onBetSlipStateChange'),
          onRecharge,
          basename: isHomeAsSportbookEnabled.value ? '' : '/sports',
        };

        window.btRender = betByFrame.initialize(config);

        if (hasChildrenDivs.value) {
          emit('changeLoadingState', false);
        }
      } catch (err) {
        console.error('🚀 Error on initializing BetBy Script', err);
      }
    },
  ]);
}

function goToBetbyPage(url: string): void {
  betByRedirectToUrl(url);
}

function goToHomePage(): void {
  goToBetbyPage('/');
}

function selectCurrentPage() {
  const url = isHomeAsSportbookEnabled.value
    ? '/'
    : sportsPageBaseUrl.value || '/';

  try {
    if (url && url !== '/') {
      return goToBetbyPage(url);
    }
  } catch {
    goToHomePage();
    throw new Error('betby: impossible to select current page');
  }
}

watch(stickyTop, () => {
  window.btRender?.updateOptions({
    betSlipOffsetTop: betSlipOffsetTop.value + DEFAULT_OFFSET_TOP,
    stickyTop: stickyTop.value,
  });
});

watch(sportBookSlugFromUrl, () => {
  selectCurrentPage();
});

watch(
  () => isAuthenticated.value,
  () => {
    setTimeout(() => {
      scrollToTop();
      location.reload();
    }, 500);
  },
);

onMounted(async () => {
  await getToken();

  selectCurrentPage();
});

onActivated(async () => {
  await getToken();

  selectCurrentPage();
});

onBeforeRouteLeave((to, from) => {
  // Ao trocar lingua no seletor, KILL e BetBy recomeça sozinho E tambem da o kill caso saia da tela da betby.
  if (to.path !== from.path) {
    window.btRender?.kill();
  }
  // emit('changeLoadingState', true);

  if (!layoutStore.menuOpen && !isLayoutMobileOrTablet.value) {
    layoutStore.toggleMenuOpen();
  }
});
</script>

<template>
  <div
    id="betby"
    ref="betbyDiv"
    :class="$style.betBy"
  ></div>
</template>

<style module lang="scss">
.betBy {
  width: 100%;
  z-index: 49;
}
</style>
